import React from "react";
import { Helmet } from "react-helmet-async";
import { NavLink } from "react-router-dom";
import parse from "html-react-parser";
import { InsightApiData } from "../../api/insights";

interface Seo {
  pageTitle: string;
  metaDescription: string;
  keyWords: string;
}

interface Img {
  src: string;
  alt: string;
}

const MONTHS: Record<string, string> = {
  january: "01", february: "02", march: "03", april: "04",
  may: "05", june: "06", july: "07", august: "08",
  september: "09", october: "10", november: "11", december: "12",
};

function toISODate(dateStr: string): string {
  const m = dateStr.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);
  if (!m) return "";
  const month = MONTHS[m[2].toLowerCase()];
  if (!month) return "";
  return `${m[3]}-${month}-${m[1].padStart(2, "0")}`;
}

export default class Insight {
  id?: string;
  heading: string = "";
  date: string;
  seo: Seo;
  img: Img;
  title: string;
  sub: string;
  preview: string;
  jsx: React.ReactNode;
  bodyHtml?: string;
  slug?: string;
  collection?: string;

  constructor(
    date: string,
    seo: Seo,
    img: Img,
    title: string,
    sub: string,
    preview: string,
    jsx: React.ReactNode,
    options?: { id?: string; bodyHtml?: string; slug?: string; collection?: string }
  ) {
    this.date = date;
    this.seo = seo;
    this.title = title;
    this.img = img;
    this.sub = sub;
    this.preview = preview;
    this.jsx = jsx;
    this.id = options?.id;
    this.bodyHtml = options?.bodyHtml;
    this.slug = options?.slug;
    this.collection = options?.collection;
  }

  static fromApi(data: InsightApiData): Insight {
    const bodyContent = data.bodyHtml
      ? parse(data.bodyHtml)
      : null;

    return new Insight(
      data.date,
      data.seo,
      data.img,
      data.title,
      data.sub,
      data.preview,
      bodyContent,
      {
        id: data.id,
        bodyHtml: data.bodyHtml,
        slug: data.slug,
        collection: data.collection,
      }
    );
  }

  get canonicalPath() {
    if (this.slug) return `/insights/${this.slug}`;
    const slug = this.title
      .toLowerCase()
      .split(" ")
      .join("-")
      .replace(/!/g, "")
      .replace(/\(/g, "")
      .replace(/\)/g, "");
    return `/insights/${slug}`;
  }

  get isoDate() {
    return toISODate(this.date);
  }

  get descriptiveAlt() {
    return `${this.title} — ${this.img.alt}`;
  }

  get category(): string {
    const t = this.title.toLowerCase();
    if (/cbam|carbon border/.test(t))
      return "CBAM & Carbon Policy";
    if (/cfd|ar[56789]|contracts? for difference|allocation round/.test(t))
      return "UK CfD & Auction Rounds";
    if (/tariff|eu ets|import dut|trade polic|anti-dumping|safeguard/.test(t))
      return "Trade & Policy";
    if (/supply chain|procurement|manufactur|fabricat|steel|shipment/.test(t))
      return "Supply Chain & Manufacturing";
    if (/float|foundation|monopile|jacket|substructure|turbine technolog/.test(t))
      return "Technology & Engineering";
    if (/scotland|scotwind|uk .* wind|british/.test(t))
      return "UK Offshore Wind";
    if (/europe|german|dutch|netherlands|france|french|denmark|danish|polish|poland/.test(t))
      return "European Offshore Wind";
    if (/china|chinese|goldwind|mingyang|cssc/.test(t))
      return "China & International Supply";
    if (/cop\d|conference|exhibition|event|hamburg|marseille|aberdeen/.test(t))
      return "Events & Conferences";
    if (/invest|financ|lend|insur|risk/.test(t))
      return "Investment & Insurance";
    if (/market|outlook|forecast|trend|headline/.test(t))
      return "Market Analysis";
    if (/weekly|week\s/.test(t))
      return "Global Offshore Wind Weekly";
    return "Offshore Wind Insights";
  }

  get tags(): string[] {
    const t = this.title.toLowerCase();
    const tags: string[] = ["offshore wind"];
    if (/cbam|carbon border|eu ets/.test(t)) tags.push("CBAM", "carbon policy");
    if (/cfd|ar[56789]|auction/.test(t)) tags.push("CfD", "UK energy policy");
    if (/supply chain|procurement/.test(t)) tags.push("supply chain");
    if (/tariff|trade|import/.test(t)) tags.push("trade policy");
    if (/steel|manufactur|fabricat/.test(t)) tags.push("manufacturing");
    if (/float/.test(t)) tags.push("floating wind");
    if (/scotland|scotwind/.test(t)) tags.push("Scotland");
    if (/uk|brit/.test(t)) tags.push("United Kingdom");
    if (/china|chinese/.test(t)) tags.push("China");
    if (/europe/.test(t)) tags.push("Europe");
    if (/invest/.test(t)) tags.push("investment");
    if (/insur/.test(t)) tags.push("insurance");
    return tags;
  }

  get InsightJSX() {
    const siteUrl = "https://www.windenergybridge.com";
    const canonicalUrl = `${siteUrl}${this.canonicalPath}`;
    const imageUrl = `${siteUrl}${this.img.src}`;

    const orgId = `${siteUrl}/#organization`;

    const articleJsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: this.seo.pageTitle,
      description: this.seo.metaDescription,
      image: imageUrl,
      url: canonicalUrl,
      datePublished: this.isoDate,
      dateModified: this.isoDate,
      articleSection: this.category,
      keywords: this.tags.join(", "),
      author: {
        "@type": "Organization",
        "@id": orgId,
        name: "WeBridge",
        url: siteUrl,
      },
      publisher: {
        "@type": "Organization",
        "@id": orgId,
        name: "WeBridge",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: `${siteUrl}/og-default.jpg` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      isPartOf: { "@type": "WebSite", "@id": `${siteUrl}/#website` },
    };

    return (
      <article
        className="insight-page-container"
        onLoad={() => {
          window.scrollTo(0, 0);
        }}
      >
        <Helmet>
          <title>{this.seo.pageTitle}</title>
          <meta name="description" content={this.seo.metaDescription} />
          <meta name="keywords" content={this.seo.keyWords} />

          <meta property="og:title" content={this.seo.pageTitle} />
          <meta property="og:description" content={this.seo.metaDescription} />
          <meta property="og:image" content={imageUrl} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="article" />
          <meta property="article:published_time" content={this.isoDate} />
          <meta property="article:section" content={this.category} />
          {this.tags.map((tag) => (
            <meta property="article:tag" content={tag} key={tag} />
          ))}

          <meta name="twitter:title" content={this.seo.pageTitle} />
          <meta name="twitter:description" content={this.seo.metaDescription} />
          <meta name="twitter:image" content={imageUrl} />

          <script type="application/ld+json">
            {JSON.stringify(articleJsonLd)}
          </script>
        </Helmet>
        {this.jsx}
      </article>
    );
  }

  get previewJSX() {
    const path = this.canonicalPath;
    const isSpecial = this.img.alt === "CBAM" || this.img.alt.includes("special");
    const isWeekly = this.collection === "WEEKLY";
    return (
      <NavLink
        to={path}
        target="_blank"
        className={`insight-container ${
          isSpecial ? "items-start" : isWeekly ? "is-weekly" : ""
        }`}
      >
        <img
          src={this.img.src}
          alt={this.descriptiveAlt}
          style={{
            objectFit: isSpecial ? "contain" : "cover",
            objectPosition: isWeekly ? "center" : "left",
          }}
          className={`${isSpecial ? "border" : ""}`}
        />
        <div>
          {this.heading === "h2" ? (
            <h2>{this.title}</h2>
          ) : (
            this.heading === "h3" && <h3>{this.title}</h3>
          )}
          <p className="sub">{this.sub}</p>
          <p className="date">{this.date}</p>
          <p className="preview">{this.preview}</p>
        </div>
      </NavLink>
    );
  }

  get smallPreviewJSX() {
    const path = this.canonicalPath;
    return (
      <NavLink
        className="inline-flex flex-col gap-2"
        to={path}
        target="_blank"
      >
        <img
          className="w-[300px] h-[200px]"
          src={this.img.src}
          alt={this.descriptiveAlt}
          style={{ objectFit: "cover", objectPosition: "left" }}
        />
        <div className="w-[300px]">
          {this.heading === "h2" ? (
            <h2 className="text-main">{this.title}</h2>
          ) : (
            this.heading === "h3" && <h3 className="text-main">{this.title}</h3>
          )}
          <p className="text-sm text-gray-600">{this.sub}</p>
          <p className="mb-4 text-gray-300 text-sm">{this.date}</p>
          <p className="text-sm text-gray-700">{this.preview}</p>
        </div>
      </NavLink>
    );
  }
}
